import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  pn,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import {
  Permission,
  PermissionAction,
  StudentServiceTemplateListDto,
  StudentServiceTemplateListResource,
  StudentServiceTemplateListFilter,
  StudentServiceTemplateFilter as StudentServiceTemplateFilterModel,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import produce from "immer";
import { studentServiceTemplatePageRoute } from "../StudentServiceTemplatePage/StudentServiceTemplatePage";
import { StudentServiceTemplateTable } from "./StudentServiceTemplateTable";
import { ListFooter } from "components";
import { StudentServiceTemplateFilter } from "./StudentServiceTemplateFilter";

const routeName = "studentServiceTemplates";

export const studentServiceTemplateListPageRoute: PageRoute = {
  handle: "QR Code",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const StudentServiceTemplateListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentServiceTemplate;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<StudentServiceTemplateListFilter>({
    sortObjects: [{ property: pn<StudentServiceTemplateListDto>("createdDate"), isDesc: true }],
    groupFilter: {
      
    },
  });
  const [initListFilter] = useState<StudentServiceTemplateListFilter>(listFilter);
  
  const [listItems, setListItems] = useState<StudentServiceTemplateListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentServiceTemplateListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const {
    tableContainerRef,
    loadList,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentServiceTemplates.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.studentServiceTemplates.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.studentServiceTemplates.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<StudentServiceTemplateListFilter>({
    maxWidth: "xl",
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <StudentServiceTemplateFilter
        filter={listFilter.groupFilter || ({} as StudentServiceTemplateFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.groupFilter = filter;
            })
          )
        }
      />
    ),
  });

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{studentServiceTemplateListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() =>
                    navigate({
                      pathname: studentServiceTemplatePageRoute.getPath({
                        id: 0,
                        crudMode: "edit",
                        screenType: "standard",
                      }),
                    })
                  }
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <StudentServiceTemplateTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              studentServiceTemplates={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
